import { Delete } from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/Info';
import { Box, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { get, isArray } from 'lodash';
import React, { FC } from 'react';
import { CreateFormProps, Field } from '../../../../lib/types/types';
import { enumT, fieldNameTranslation, isTrue, tooltipTranslate } from '../../../../utils/helper';
// import { DisplayDate, DisplayDateTime, DisplayDecimal, DisplayEuroMUI } from '../../../../utils/formats';
import ReactMarkdown from 'react-markdown';
import { useLocation } from 'react-router-dom';
import { FORM_FIELDS_TYPES, MAX_COLUMNS_HORIZONTAL_LAYOUT } from '../../../../lib/constants/constant';
import { DisplayDate, DisplayDecimal, DisplayEuro } from '../../../../utils/formats';
import SingleUpload from '../form-fields/fields/SingleUpload';
import FormFields from '../form-fields/FormFields';

const preprocessMarkdown = (text: any) => {
	return text.replace(/^(\d+)\./gm, '$1\\.'); // Wandelt "1." in "1\." um
};

const CreateTable: FC<CreateFormProps & {}> = (props) => {
	const location = useLocation();
	const { formStruc, formValues, formErrors, updateFormValuesFunc, readOnly, columnsLabel, onDeleteFunc, printMode } = props;

	const allColumnsLabel = columnsLabel || [];
	const shouldUseVerticalPrintLayout = printMode && allColumnsLabel.length > MAX_COLUMNS_HORIZONTAL_LAYOUT;

	const getColumnHeaderLabel = (column: string | Record<string, any> | undefined, fallback = '') => {
		if (typeof column === 'object' && column !== null) {
			return get(column, 'label', fallback);
		}

		if (typeof column === 'string') {
			return column;
		}

		return fallback;
	};

	const RenderColumnCell: FC<{ eachColumn: string | Record<string, any> }> = ({ eachColumn }) => {
		const baseMinWidth = printMode ? '2rem' : '11rem';
		if (typeof eachColumn === 'object') {
			return (
				<TableCell
					sx={{
						minWidth: get(eachColumn, 'width', baseMinWidth),
						whiteSpace: printMode ? 'normal' : 'nowrap',
						padding: printMode ? '2px 4px' : undefined,
					}}
				>
					{' '}
					{get(eachColumn, 'label', '')}
				</TableCell>
			);
		} else {
			return (
				<TableCell
					sx={{
						minWidth: baseMinWidth,
						maxWidth: printMode ? 'auto' : '11rem',
						whiteSpace: printMode ? 'normal' : 'nowrap',
						padding: printMode ? '2px 4px' : 'undefined',
					}}
				>
					{' '}
					{eachColumn}
				</TableCell>
			);
		}
	};

	const RenderCellTableBody: FC<{ fieldProps: Field[] }> = ({ fieldProps }) => {
		if (get(fieldProps, 'type', '') === 'label' || get(fieldProps, 'type', '') === '') {
			// translation the strapi-field-name
			const dataPathParts = get(fieldProps, 'path', '').split('.');
			const strapiFieldName = dataPathParts.length > 0 ? dataPathParts[dataPathParts.length - 1] : get(fieldProps, 'path', '');
			const apiName = get(props, 'api') ? get(props, 'api', '') : get(location, 'state.api');
			const labelTranlated = fieldNameTranslation(strapiFieldName, apiName);

			const fieldLabel: any = isTrue(get(fieldProps, 'hideLabel')) ? '' : get(fieldProps, 'label', labelTranlated);
			const toolTip = tooltipTranslate(strapiFieldName, apiName);

			return (
				<Stack direction={'row'} spacing={1} alignItems={'center'}>
					<ReactMarkdown>{preprocessMarkdown(fieldLabel)}</ReactMarkdown>

					{(get(fieldProps, 'tooltip') || toolTip) && (
						<Tooltip
							title={<ReactMarkdown>{get(fieldProps, 'tooltip', '') || toolTip}</ReactMarkdown>}
							arrow
							placement='top'
							sx={{ cursor: 'pointer' }}
						>
							<InfoIcon />
						</Tooltip>
					)}
				</Stack>
			);
		}

		return <></>;
	};

	const PrintTableBody: FC = () => {
		if (shouldUseVerticalPrintLayout) {
			const verticalRowsPerBlock = 2;
			const groupedRows = (formStruc || []).reduce((acc: any[][], eachRow: any, index: number) => {
				const blockIndex = Math.floor(index / verticalRowsPerBlock);
				if (!acc[blockIndex]) {
					acc[blockIndex] = [];
				}
				acc[blockIndex].push(eachRow);
				return acc;
			}, []);

			return (
				<TableContainer
					component={Box}
					sx={{
						width: '100%',
						maxWidth: '100%',
						overflowX: 'auto',
						paddingBottom: '4px',
						'@media print': {
							breakInside: 'auto',
							pageBreakInside: 'auto',
						},
					}}
				>
					<Box
						sx={{
							width: '100%',
							display: 'flex',
							flexDirection: 'column',
							gap: 1,
						}}
					>
						{groupedRows.map((rowGroup: any[], groupIndex: number) => {
							return (
								<Box
									key={groupIndex}
									sx={{
										// border: '1px solid #EAF0F1',
										// borderRadius: '4px',
										padding: '6px 8px',
										breakInside: 'avoid-page',
										pageBreakInside: 'avoid',
									}}
								>
									<Stack spacing={0}>
										{get(rowGroup?.[0], 'fields', []).map((_fieldProp: any, columnIndex: number) => {
											const columnLabel = getColumnHeaderLabel(get(allColumnsLabel, columnIndex), `Spalte ${columnIndex + 1}`);

											return (
												<Box
													key={columnIndex}
													sx={{
														display: 'grid',
														gridTemplateColumns: `minmax(8rem, 35%) repeat(${rowGroup.length}, minmax(8rem, 1fr))`,
														columnGap: 1,
														alignItems: 'start',
														position: 'relative',
														py: 0.5,
														'&::before': {
															content: '""',
															display: columnIndex === 0 ? 'none' : 'block',
															position: 'absolute',
															left: 6,
															right: 6,
															top: 0,
															borderTop: '1px solid #F3F6F7',
														},
													}}
												>
													<Typography
														component='span'
														sx={{
															fontSize: '0.8125rem',
															fontWeight: 600,
															lineHeight: 1.3,
														}}
													>
														{columnLabel}
													</Typography>
													{rowGroup.map((groupRow: any, rowGroupIndex: number) => {
														const fieldProp = get(groupRow, `fields.${columnIndex}`, {});
														const path = get(fieldProp, 'path');
														const type = get(fieldProp, 'type');
														const format = get(fieldProp, 'format');
														let fieldvalue = get(formValues, path, '-');

														if (typeof fieldvalue === 'object' && fieldvalue !== null) {
															const options = get(fieldProp, 'options', []);
															const id = get(fieldvalue, 'id', '');
															const matchedOption = options?.find((option: any) => get(option, 'value', '') === id);
															if (matchedOption) {
																fieldvalue = get(matchedOption, 'label', fieldvalue);
															}
														}

														return (
															<Typography
																key={rowGroupIndex}
																component='span'
																sx={{
																	fontSize: '0.8125rem',
																	display: 'block',
																	whiteSpace: 'normal',
																	overflowWrap: 'anywhere',
																	wordBreak: 'break-word',
																}}
															>
																{getDisplayText(type, String(fieldvalue), format)}
															</Typography>
														);
													})}
												</Box>
											);
										})}
									</Stack>
								</Box>
							);
						})}
					</Box>
				</TableContainer>
			);
		}

		return (
			<>
				<TableContainer
					component={Box}
					sx={{
						width: '100%',
						maxWidth: '100%',
						overflowX: 'auto',
						paddingBottom: '4px',
						'@media print': {
							breakInside: 'avoid-page',
							pageBreakInside: 'avoid',
						},
					}}
				>
					<Table
						size={printMode ? 'small' : 'medium'}
						sx={{
							width: '100%',
							tableLayout: '',
							...(printMode ? { borderCollapse: 'collapse' } : {}),
							...(printMode ? { border: 'none' } : {}),
						}}
					>
						{isArray(allColumnsLabel) && allColumnsLabel.length > 0 ?
							<>
								<TableHead>
									<TableRow>
										{allColumnsLabel.map((ele: string | Record<string, any>, i: number) => {
											return <RenderColumnCell key={i} eachColumn={ele} />;
										})}
										{isTrue(get(props, 'allowDeleteFormValues')) && !printMode && (
											<TableCell className='pinned-right-column'>Aktion</TableCell>
										)}
									</TableRow>
								</TableHead>
							</>
						:	null}
						<TableBody>
							{(formStruc || []).map((eachFieldRow: any, rowIndex: number) => {
								return (
									<TableRow key={rowIndex}>
										{get(eachFieldRow, 'fields', []).map((fieldProp: any, columnIndex: number) => {
											const path = get(fieldProp, 'path');
											const type = get(fieldProp, 'type');
											const format = get(fieldProp, 'format');
											let fieldvalue = get(formValues, path, '-');
											if (typeof fieldvalue === 'object' && fieldvalue !== null) {
												const options = get(fieldProp, 'options', []);
												const id = get(fieldvalue, 'id', '');
												const matchedOption = options?.find((option: any) => get(option, 'value', '') === id);
												if (matchedOption) {
													fieldvalue = get(matchedOption, 'label', fieldvalue);
												}
											}
											const isLastRow = rowIndex === (formStruc || []).length - 1;
											return (
												<TableCell
													key={columnIndex}
													align={['integer', 'decimal', 'date'].includes(type) ? 'right' : 'left'}
													sx={{
														borderBottom: isLastRow ? 'none' : '1px solid #EAF0F1',
														py: 0,
														pl: 0,
														pr: 1.5,
														verticalAlign: 'top',
													}}
												>
													<Typography
														component='span'
														sx={{
															fontSize: '0.875rem',
															display: 'block',
															whiteSpace: 'normal',
															overflowWrap: 'anywhere',
															wordBreak: 'break-word',
														}}
													>
														{getDisplayText(type, String(fieldvalue), format)}
													</Typography>
												</TableCell>
											);
										})}
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			</>
		);
	};

	if (printMode) {
		return <PrintTableBody />;
	}

	const handleDelete = async (rowIndex: number) => {
		if (isTrue(get(props, 'allowDeleteFormValues'))) {
			// Pfad von einem Feld, damit man dann die richtige Object zum Löschen in Antrag(objekt) finden kann
			let selectedRowFieldsFullPath = '';

			// jede Zelle in die Schleife gehen und die Pfade finden.
			// Es kann sein, dass die erste Splate nur als Info Text ist. Daher wird kein Pfad da sein.
			for (let i = 0; i < get(formStruc[rowIndex], 'fields', []).length; i++) {
				if (get(formStruc[rowIndex], `fields.${i}.type`)) {
					selectedRowFieldsFullPath = get(formStruc[rowIndex], `fields.${i}.path`, '');
					break;
				}
			}

			// Die letze attributes ausschneiden. Damit man die Pfad für den Object rausfinden kann, der man gerade zum löschen ausgewählt hat.
			const lastAttributesIndex = selectedRowFieldsFullPath.lastIndexOf('.attributes');
			const path = selectedRowFieldsFullPath.substring(0, lastAttributesIndex);
			// Nach dem pfad gefunden wurde, dann aus dem Antrag die Objekt rufen, die dann gelöscht wird.
			const toDeleteObject = get(formValues, `${path}`, {});

			// Diese Funktion ist im jeden Komponente extra definiert.
			// Weil man nicht weiß, welche "collection-Type" gelöscht werden soll, wird hier einfach das Object und die pfad zum Objekt als Parameter übergeben.
			// Path ist auch notwendig, wenn man dann den "toDeleteObject" aus dem Antrag raus löschen muss. Dafür ist die beiden Parameter immer notwendig.
			// Mit der Zeit bzw. Afo kann weitere Parameter hier geben. Deswegen wird hier die Parameter als Objekt übergeben
			// Prinzip ist genau das gleich wie die params in MUI-DateGrid component
			onDeleteFunc({ toDeleteObject, path });
		}
	};
	return (
		<>
			<TableContainer
				component={Box}
				sx={{
					width: '100%',
					maxWidth: '100%',
					overflowX: printMode ? 'visible' : 'auto',
					'&::-webkit-scrollbar': {
						height: '16px !important',
						width: '12px',
						color: '#333',
					},
					'&::-webkit-scrollbar-track': {
						background: '#fff',
						borderRadius: '3px',
					},
					'&::-webkit-scrollbar-thumb': {
						background: '#777',
						borderRadius: '3px',
						border: '2px solid #f1f1f1',
					},
					'&::-webkit-scrollbar-thumb:hover': {
						background: '#333',
					},
					paddingBottom: printMode ? '4px' : '16px',
					...(printMode ?
						{
							border: '1px solid #e0e0e0',
							borderRadius: '4px',
							marginBottom: '6px',
						}
					:	{}),
				}}
			>
				<Table
					size={printMode ? 'small' : 'medium'}
					sx={{
						width: '100%',
						tableLayout: printMode ? 'auto' : '',
						...(printMode ? { borderCollapse: 'collapse' } : {}),
						...(printMode ? { border: 'none' } : {}),
					}}
				>
					{isArray(allColumnsLabel) && allColumnsLabel.length > 0 ?
						<>
							<TableHead>
								<TableRow>
									{allColumnsLabel.map((ele: string | Record<string, any>, i: number) => {
										return <RenderColumnCell key={i} eachColumn={ele} />;
									})}
									{isTrue(get(props, 'allowDeleteFormValues')) && !printMode && (
										<TableCell className='pinned-right-column'>Aktion</TableCell>
									)}
								</TableRow>
							</TableHead>
						</>
					:	null}
					<TableBody>
						{(formStruc || []).map((eachFieldRow: any, rowIndex: number) => {
							return (
								<TableRow
									key={rowIndex}
									sx={{
										borderBottom: '1.5px solid rgba(224, 224, 224, 1)',
										...(printMode ? { '& > td': { padding: '4px 6px' } } : {}),
									}}
								>
									{get(eachFieldRow, 'fields', []).map((fieldProp: any, columnIndex: number) => {
										return (
											<TableCell
												key={columnIndex}
												sx={{
													borderBottom: 'unset',
													padding: printMode ? '4px 6px' : '8px 8px 4px 8px',
												}}
											>
												{get(fieldProp, 'type', '') === 'label' || get(fieldProp, 'type', '') === '' ?
													<>
														<RenderCellTableBody fieldProps={fieldProp} />
													</>
												:	<>
														<FormFields
															type={get(fieldProp, 'type')}
															formValues={formValues}
															formErrors={formErrors}
															onChange={updateFormValuesFunc}
															readOnly={readOnly ? readOnly : false}
															hideLabel={true}
															size={get(props, 'size', 'small')}
															{...fieldProp}
														/>
													</>
												}
											</TableCell>
										);
									})}
									{/* Render Aktion column only when delete is allowed and not in print mode */}
									{isTrue(get(props, 'allowDeleteFormValues')) && !printMode && (
										<TableCell className='pinned-right-column'>
											<IconButton onClick={() => handleDelete(rowIndex)} disabled={readOnly}>
												<Delete />
											</IconButton>
										</TableCell>
									)}
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</>
	);
};

export default CreateTable;

function getDisplayText(type: string, value: any, format?: string, label?: string) {
	if (value === undefined) return '';

	if (label) return label;
	switch (type) {
		case 'decimal':
			if (format === 'currency') {
				return DisplayEuro(value);
			}
			return DisplayDecimal(value);
		case 'date':
			return DisplayDate(value);
		case 'enumeration':
			return enumT(value);
		case 'boolean':
			return isTrue(value) ? 'Ja' : 'Nein';
		case FORM_FIELDS_TYPES.media:
			console.log(' value in formfields type media ', value);
			return <SingleUpload label={''} fieldValue={value} updateFieldValue={(_e) => {}} isDisabled={true} error={false} errorMessage={''} />;
		default:
			return value;
	}
}
